import { useCallback, useEffect, useState } from "react";
import { MemberLayout } from "../../components/Layouts";
import { api, readUser, fmt, errMsg } from "../../lib/api";

export default function Withdrawals() {
  const [user, setUser] = useState(readUser());
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ amount: "", wallet_address: "" });
  const [notice, setNotice] = useState({ ok: false, msg: "" });
  const [eligibility, setEligibility] = useState({ eligible: false, message: "Checking withdrawal availability..." });
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    const [me, w, s, e] = await Promise.all([
      api.get("/auth/me"),
      api.get("/withdrawals"),
      api.get("/settings/public"),
      api.get("/withdrawals/eligibility"),
    ]);
    setUser(me.data); localStorage.setItem("ha_user", JSON.stringify(me.data));
    setRows(w.data); setSettings(s.data || {});
    setEligibility(e.data || {});
    setForm((f) => ({ ...f, wallet_address: me.data.wallet_address || f.wallet_address }));
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => {
    if (Number(eligibility.remaining_seconds || 0) <= 0) return undefined;
    const timer = window.setInterval(() => {
      setEligibility((value) => ({ ...value, remaining_seconds: Math.max(0, Number(value.remaining_seconds || 0) - 1) }));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [eligibility.remaining_seconds]);

  async function submit(e) {
    e.preventDefault();
    setNotice({ ok: false, msg: "" });
    try {
      if (!eligibility.eligible) throw new Error(eligibility.message || "Withdrawal is not available.");
      if (!Number.isFinite(Number(form.amount)) || Number(form.amount) <= 0) throw new Error("Enter a valid withdrawal amount.");
      if (!/^0x[a-fA-F0-9]{40}$/.test(form.wallet_address.trim())) throw new Error("Enter a valid BEP20 wallet address (0x followed by 40 hexadecimal characters).");
      setConfirming(true);
      await api.post("/withdrawals/confirm", { amount: Number(form.amount), wallet_address: form.wallet_address.trim() }, { timeout: 20000 });
      setNotice({ ok: true, msg: "Withdrawal request submitted successfully. The administrator will review it shortly." });
      setForm((value) => ({ amount: "", wallet_address: value.wallet_address }));
      load().catch(() => {});
    } catch (e) { setNotice({ ok: false, msg: errMsg(e) }); }
    finally { setConfirming(false); }
  }

  const feePercent = Number(settings.withdrawal_fee ?? 3);
  const fee = Number(form.amount || 0) * feePercent / 100;
  const net = Math.max(0, Number(form.amount || 0) - fee);
  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const DAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const openDays = Array.isArray(settings.withdrawal_days) ? [...settings.withdrawal_days].map(Number).sort((a, b) => a - b) : [0, 1, 2, 3, 4, 5, 6];
  const allOpen = openDays.length === 7;
  const todayIdx = new Date().getDay();
  const openToday = openDays.includes(todayIdx);
  const dailyCap = Number(settings.max_daily_withdrawal || 0);
  const formatWait = (seconds) => {
    const total = Math.max(0, Number(seconds || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <MemberLayout user={user} active="Withdrawals">
      <section className="deposit-grid">
        <div className="panel">
          <div className="panel-heading">
            <div><span className="eyebrow">WITHDRAW USDT</span><h3>Send to your wallet</h3></div>
            {!allOpen && (
              <span className={`day-badge ${openToday ? "open" : "closed"}`} data-testid="withdraw-days-badge" title={openToday ? "Withdrawals are open today" : `Today (${DAY_FULL[todayIdx]}) is closed — try again on an open day`}>
                {openToday ? "Open today · " : "Closed today · "}{openDays.map((d) => DAY_LABELS[d]).join(", ")}
              </span>
            )}
          </div>
          <div className="deposit-info">
            <div className="deposit-row"><span>Available balance</span><strong data-testid="withdraw-available">${fmt(user?.balance, 3)}</strong></div>
            <div className="deposit-row"><span>Minimum withdrawal</span><strong>${fmt(settings.minimum_withdrawal)}</strong></div>
            <div className="deposit-row"><span>Withdrawal fee</span><strong data-testid="withdraw-fee-percent">{fmt(feePercent)}% (${fmt(fee, 3)})</strong></div>
            {dailyCap > 0 && <div className="deposit-row"><span>Daily cap</span><strong data-testid="withdraw-daily-cap">${fmt(dailyCap)}</strong></div>}
            <div className="deposit-row"><span>Net amount</span><strong data-testid="withdraw-net">${fmt(net, 3)}</strong></div>
          </div>
          <div className={eligibility.eligible ? "success" : "error"} data-testid="withdraw-eligibility">
            {eligibility.message}{!eligibility.eligible && eligibility.remaining_seconds ? ` Available in ${formatWait(eligibility.remaining_seconds)}.` : ""}
          </div>
          <form onSubmit={submit} className="deposit-form" noValidate>
            <label>Amount (USDT)<input data-testid="withdraw-amount" type="number" min="0" step="0.001" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></label>
            <label>BEP20 wallet address<input data-testid="withdraw-wallet" minLength={42} maxLength={42} pattern="0x[a-fA-F0-9]{40}" value={form.wallet_address} onChange={(e) => setForm({ ...form, wallet_address: e.target.value })} required /></label>
            {notice.msg && <div className={notice.ok ? "success" : "error"} role="alert" data-testid="withdraw-notice">{String(notice.msg)}</div>}
            <button className="btn btn-accent full" data-testid="withdraw-submit" disabled={!eligibility.eligible || confirming}>{confirming ? "Processing..." : "Request withdrawal"}</button>
          </form>
        </div>
        <div className="panel activity">
          <div className="panel-heading"><div><span className="eyebrow">LIFECYCLE</span><h3>How it works</h3></div></div>
          <ol className="lifecycle">
            <li>You request a withdrawal (Pending)</li>
            <li>Administrator reviews and approves (Processing)</li>
            <li>Blockchain transfer is executed and completed (Completed)</li>
            <li>If rejected, funds are returned to your balance</li>
          </ol>
        </div>
      </section>
      <div className="table-panel">
        <div className="panel-heading"><div><span className="eyebrow">HISTORY</span><h3>My withdrawals</h3></div></div>
        <div className="table-wrap">
          <table className="member-withdrawals-table">
            <thead><tr><th>Date</th><th>Amount</th><th>Fee</th><th>Net</th><th>Wallet</th><th>Status</th><th>TX Hash</th></tr></thead>
            <tbody>
              {rows.map((w, i) => (
                <tr key={i} data-testid={`withdrawal-row-${i}`}>
                  <td data-label="Date">{new Date(w.created_at).toLocaleString()}</td>
                  <td data-label="Amount">${fmt(w.amount)}</td>
                  <td data-label="Fee">${fmt(w.fee)}</td>
                  <td data-label="Net">${fmt(w.net_amount)}</td>
                  <td data-label="Wallet" className="hash">{(w.wallet_address || "").slice(0, 10)}…</td>
                  <td data-label="Status"><span className={`status ${w.status.toLowerCase()}`}>{w.status}</span></td>
                  <td data-label="TX Hash" className="hash">{(w.tx_hash || "").slice(0, 12) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="table-empty">No withdrawals yet.</div>}
        </div>
      </div>
    </MemberLayout>
  );
}
